import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail, MessageCircle, Zap, Check } from "lucide-react";
import { toast } from "sonner";
import { staffApi } from "@/lib/staffApi";

/**
 * ReadyEarlyModal — the "let the client know I'm ready" confirmation panel.
 *
 * Props:
 *   appointment      — shaped appt object (needs bookingStart, customer, etc.)
 *   onClose()        — dismiss handler
 *   onSent()         — called on successful send/queue; parent should refresh
 *   defaultChannel   — 'email' | 'whatsapp' (auto if omitted)
 */
export function ReadyEarlyModal({ appointment, onClose, onSent, defaultChannel }) {
    const cust = appointment.customer || {};
    const firstName = cust.firstName || "there";
    const hasEmail = !!(cust.email || "").trim();
    const hasPhone = !!(cust.phone || "").trim();

    // Time helpers
    const now = new Date();
    const originalTime = useMemo(() => {
        try {
            const d = new Date((appointment.bookingStart || "").replace(" ", "T"));
            return d;
        } catch { return null; }
    }, [appointment.bookingStart]);

    const [offset, setOffset] = useState("now");            // 'now' | '5' | '10' | 'custom'
    const [customHHMM, setCustomHHMM] = useState(() => hhmm(now));

    const availableFrom = useMemo(() => {
        if (offset === "custom") return customHHMM;
        const d = new Date(now.getTime());
        if (offset === "5")  d.setMinutes(d.getMinutes() + 5);
        if (offset === "10") d.setMinutes(d.getMinutes() + 10);
        return hhmm(d);
    }, [offset, customHHMM]);

    const [channel, setChannel] = useState(defaultChannel || (hasEmail ? "email" : "whatsapp"));
    const [message, setMessage] = useState(() =>
        defaultMessage(firstName, formatTime(originalTime), availableFrom),
    );
    const [saving, setSaving] = useState(false);
    const [alreadySent, setAlreadySent] = useState(null); // { channel, sent_at } | null

    // Refresh the default message when key inputs change (respect manual edits).
    const [manuallyEdited, setManuallyEdited] = useState(false);
    useEffect(() => {
        if (manuallyEdited) return;
        setMessage(defaultMessage(firstName, formatTime(originalTime), availableFrom));
    }, [firstName, originalTime, availableFrom, manuallyEdited]);

    // Check duplicate on mount
    useEffect(() => {
        staffApi.getReadyEarly(appointment.id)
            .then((r) => { if (r.sent) setAlreadySent(r); })
            .catch(() => {});
    }, [appointment.id]);

    const send = async (force = false) => {
        setSaving(true);
        try {
            const r = await staffApi.sendReadyEarly(appointment.id, {
                available_from: availableFrom,
                channel, message,
            }, force);
            if (r.channel === "whatsapp") {
                toast.success("WhatsApp notification queued for automatic delivery.");
            } else {
                toast.success("Email notification queued for delivery.");
            }
            onSent?.(r);
        } catch (e) {
            if (e.status === 409) {
                if (window.confirm("A Ready-Early notification has already been sent. Send again?")) {
                    return send(true);
                }
            } else {
                toast.error(e.message || "Could not send notification.");
            }
        } finally { setSaving(false); }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center bg-navy/90 backdrop-blur-sm p-2 pb-24 sm:p-4 overflow-y-auto overscroll-contain"
            data-testid="ready-early-modal"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
        >
            <div
                className="w-full max-w-lg my-2 sm:my-auto rounded-2xl bg-navy border-2 border-gold/50 p-4 sm:p-6 relative shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button type="button" onClick={onClose} aria-label="Close" data-testid="ready-early-close"
                        className="absolute top-3 right-3 text-white/60 hover:text-white">
                    <X className="h-5 w-5" />
                </button>

                <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85 flex items-center gap-1.5">
                    <Zap className="h-3 w-3" /> Ready Early
                </div>
                <h2 className="font-serif text-2xl text-white mt-1">Let {firstName} know</h2>

                {alreadySent && (
                    <div className="mt-3 rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-3 text-emerald-100 text-xs flex items-center gap-2" data-testid="ready-early-already-sent">
                        <Check className="h-4 w-4" />
                        Already sent via <strong>{alreadySent.channel}</strong> at {formatTimeIso(alreadySent.sent_at)}.
                        Available from <strong>{alreadySent.available_from}</strong>.
                    </div>
                )}

                {/* Appointment summary */}
                <dl className="mt-4 grid grid-cols-1 min-[380px]:grid-cols-2 gap-2">
                    <SummaryItem label="Customer" value={`${cust.firstName || ""} ${cust.lastName || ""}`.trim() || "—"} />
                    <SummaryItem label="Treatment" value={appointment.serviceName || appointment.service?.name || "—"} />
                    <SummaryItem label="Original time" value={formatTime(originalTime) || "—"} />
                    <SummaryItem label="Current time" value={hhmm(now)} />
                </dl>

                {/* Offset picker */}
                <div className="mt-4">
                    <div className="text-xs font-bold uppercase tracking-[0.16em] text-white mb-2">Available from</div>
                    <div className="grid grid-cols-4 gap-1.5">
                        {[
                            { k: "now",    label: "Now" },
                            { k: "5",      label: "+5 min" },
                            { k: "10",     label: "+10 min" },
                            { k: "custom", label: "Custom" },
                        ].map((o) => (
                            <button key={o.k} type="button"
                                    onClick={() => setOffset(o.k)}
                                    data-testid={`ready-early-offset-${o.k}`}
                                    className={`rounded-xl py-2.5 px-1 text-xs font-bold border-2 transition-colors ${offset === o.k ? "border-gold bg-gold/20 text-gold" : "border-white/25 bg-white/[0.04] text-white hover:border-white/50"}`}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                    {offset === "custom" && (
                        <input type="time" value={customHHMM} onChange={(e) => setCustomHHMM(e.target.value)}
                               data-testid="ready-early-custom-time"
                               className="mt-2 w-40 bg-transparent border-b border-white/25 text-white outline-none focus:border-gold py-1 text-sm" />
                    )}
                </div>

                {/* Channel picker */}
                <div className="mt-4">
                    <div className="text-xs font-bold uppercase tracking-[0.16em] text-white mb-2">Channel</div>
                    <div className="grid grid-cols-2 gap-1.5">
                        <ChannelButton icon={Mail} label="Email" ok={hasEmail}
                                       selected={channel === "email"} onClick={() => setChannel("email")}
                                       testId="ready-early-ch-email" />
                        <ChannelButton icon={MessageCircle} label="WhatsApp" ok={hasPhone}
                                       selected={channel === "whatsapp"} onClick={() => setChannel("whatsapp")}
                                       testId="ready-early-ch-whatsapp" />
                    </div>
                    {channel === "whatsapp" && (
                        <div className="mt-2 text-xs font-semibold text-white/80 flex items-center gap-1.5">
                            <Check className="h-3 w-3 text-gold" />
                            Sends automatically using the approved Pout WhatsApp template.
                        </div>
                    )}
                </div>

                {/* Message */}
                <div className="mt-4">
                    <div className="text-xs font-bold uppercase tracking-[0.16em] text-white mb-2">
                        {channel === "whatsapp" ? "WhatsApp message preview" : "Message"}
                    </div>

                    {channel === "whatsapp" ? (
                        <>
                            <div
                                data-testid="ready-early-whatsapp-preview"
                                className="w-full rounded-xl border-2 border-gold/30 bg-white/[0.04] p-3 text-sm font-semibold leading-relaxed text-white whitespace-pre-wrap"
                            >
                                {whatsAppTemplatePreview(
                                    firstName,
                                    appointment.serviceName || appointment.service?.name || "treatment",
                                    availableFrom,
                                    formatTime(originalTime),
                                )}
                            </div>
                            <div className="text-[11px] font-semibold text-white/60 mt-1">
                                WhatsApp template wording is fixed by Meta. Appointment details are inserted automatically.
                            </div>
                        </>
                    ) : (
                        <>
                            <textarea rows={5} value={message}
                                      onChange={(e) => { setMessage(e.target.value); setManuallyEdited(true); }}
                                      data-testid="ready-early-message"
                                      className="w-full rounded-xl border-2 border-white/20 bg-white/[0.04] p-3 text-sm font-semibold leading-relaxed text-white placeholder-white/40 outline-none focus:border-gold resize-y" />
                            <div className="text-[11px] font-semibold text-white/60 mt-1">{message.length} characters</div>
                        </>
                    )}
                </div>

                <div className="mt-5 flex flex-col-reverse min-[380px]:flex-row min-[380px]:justify-end gap-2 pb-1">
                    <button type="button" onClick={onClose} className="rounded-xl h-11 px-5 border border-white/25 text-white font-bold text-sm hover:border-white/50">
                        Cancel
                    </button>
                    <button type="button" disabled={saving || (channel === "email" && !message.trim())}
                            onClick={() => send(false)}
                            data-testid="ready-early-send"
                            className="rounded-xl h-11 px-5 bg-gold text-navy font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-1.5">
                        {saving ? "Sending…" : (alreadySent ? "Send again" : "Send notification")}
                        <Zap className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function SummaryItem({ label, value }) {
    return (
        <div className="rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2.5 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/70">{label}</div>
            <div className="mt-0.5 text-sm font-bold text-white leading-snug break-words">{value}</div>
        </div>
    );
}

function ChannelButton({ icon: Icon, label, selected, onClick, ok, testId }) {
    return (
        <button type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={!ok} data-testid={testId}
                className={`flex items-center gap-2 rounded-xl border-2 p-3 text-sm font-bold transition-colors
                    ${selected ? "border-gold bg-gold/20 text-white ring-1 ring-gold/30" : "border-white/25 bg-white/[0.04] text-white hover:border-white/50"}
                    ${!ok ? "opacity-40 cursor-not-allowed" : ""}`}>
            <Icon className="h-4 w-4" />
            <div className="text-left">
                <div>{label}</div>
                {!ok && <div className="text-[10px] text-white/50">No contact on file</div>}
            </div>
        </button>
    );
}

function hhmm(d) {
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getHours())}:${p(d.getMinutes())}`;
}
function formatTime(d) {
    if (!d) return "";
    return hhmm(d);
}
function formatTimeIso(iso) {
    try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}
function whatsAppTemplatePreview(firstName, treatment, availableFrom, originalTime) {
    return (
        `Hi ${firstName}, Angela is ready a little early for your ${treatment} appointment today. ` +
        `You're welcome to arrive from ${availableFrom} if convenient. Your original appointment ` +
        `time of ${originalTime} remains booked if you'd prefer to keep it. — Pout Aesthetics`
    );
}

function defaultMessage(firstName, originalTime, availableFrom) {
    return (
`Hi ${firstName}, Angela has finished slightly earlier than expected and is ready for you now. ` +
`You're welcome to come in early from ${availableFrom} if convenient, but there's no problem if ` +
`you prefer to keep your original appointment time of ${originalTime}.

Pout Aesthetics`
    );
}
